<template>
  <div>

    <!-- Organization Information -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.org_info') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.nationality') }}</b>
            <div class="mg-t-11px">
              {{ newForm.nationality }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.country') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCountry }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.your_company_summary') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCompanyName }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.tax_id') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgTaxId }}
            </div>
          </div>

          <div class="mg-t-31px">
            <b>{{ $t('kyc.business_type') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgBusinessType | displayFromKeyToText(businessList, $i18n.locale) }}
            </div>
          </div>
          <div
            v-if="newForm.averageIncome"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.avg_income') }}</b>
            <div class="mg-t-11px">
              {{ newForm.averageIncome | displayFromKeyToText(averageIncomeList, $i18n.locale) }}
            </div>
          </div>
          <div
            v-if="newForm.proofOfIncome"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.proof_of_income') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.proofOfIncome.name }}
            </div>
          </div>
          <div
            v-if="newForm.assetOwnership"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.evidence_assets') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.assetOwnership.name }}
            </div>
          </div>

          <div class="mg-t-31px">
            <b>{{ $t('kyc.address') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCompanyAddressLineOne }}
            </div>
          </div>

          <div class="mg-t-31px">
            <b>{{ $t('kyc.address_line_2_summary') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCompanyAddressLineTwo || '-' }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.province') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCompanyProvince| displayFromKeyToText(provincesInThailand, $i18n.locale) }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.city') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCompanyCity }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.zipcode') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgCompanyPostcode }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.tel_number') }}</b>
            <div class="mg-t-11px">
              {{ newForm.orgTel }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Information -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.auth_represent_info') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-31px">
            <b>{{ $t('kyc.name_title') }}</b>
            <div
              v-if="newForm.nameTitle === 'Mr.'"
              class="mg-t-11px"
            >
              {{ $t('kyc.mr') }}
            </div>
            <div
              v-else-if="newForm.nameTitle === 'Miss.'"
              class="mg-t-11px"
            >
              {{ $t('kyc.miss') }}
            </div>
            <div
              v-else
              class="mg-t-11px"
            >
              {{ $t('kyc.mrs') }}
            </div>
          </div>
          <div
            v-if="newForm.firstNameInThai"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.first_name_th') }}</b>
            <div class="mg-t-11px">
              {{ newForm.firstNameInThai }}
            </div>
          </div>
          <div
            v-if="newForm.lastNameInThai"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.last_name_th') }}</b>
            <div class="mg-t-11px">
              {{ newForm.lastNameInThai }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.first_name') }}</b>
            <div class="mg-t-11px">
              {{ newForm.firstName }}
            </div>
          </div>
          <div
            v-if="newForm.middleName"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.middle_name_summary') }}</b>
            <div class="mg-t-11px">
              {{ newForm.middleName }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.last_name') }}</b>
            <div class="mg-t-11px">
              {{ newForm.lastName }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.gender') }}</b>
            <div class="mg-t-11px">
              {{ newForm.gender === 'Male' ? $t('kyc.male') : $t('kyc.female') }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.marital_status') }}</b>
            <div class="mg-t-11px">
              {{ newForm.maritalStatus === 'Single' ? $t('kyc.single') : $t('kyc.married') }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.tel_number') }}</b>
            <div class="mg-t-11px">
              {{ newForm.tel }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.date_of_birth') }}</b>
            <div class="mg-t-11px">
              {{ newForm.birthdate | formatDate }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.occupation') }}</b>
            <div class="mg-t-11px">
              {{ newForm.career | displayFromKeyToText(occupationList, $i18n.locale) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Origin Address -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.auth_represent_addr') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-31px">
            <b>{{ $t('kyc.origin_address') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originAddressLineOne }}
            </div>
          </div>
          <div class="mg-t-31px">
            <b>{{ $t('kyc.address_line_2_summary') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originAddressLineTwo || '-' }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.country') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originCountry }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.province') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originProvince | displayFromKeyToText(provincesInThailand, $i18n.locale) }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.city') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originCity }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.zipcode') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originPostcode }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Current Address -->

    <div class="columns">
      <div class="column">
        <div class="pd-l-12pct">
          <div class="mg-t-31px">
            <b>{{ $t('kyc.current_address') }}</b>
            <div class="mg-t-11px">
              {{ newForm.currentAddressLineOne }}
            </div>
          </div>
          <div class="mg-t-31px">
            <b>{{ $t('kyc.address_line_2_summary') }}</b>
            <div class="mg-t-11px">
              {{ newForm.currentAddressLineTwo || '-' }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.country') }}</b>
            <div class="mg-t-11px">
              {{ newForm.originCountry }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.province') }}</b>
            <div class="mg-t-11px">
              {{ newForm.currentProvince | displayFromKeyToText(provincesInThailand, $i18n.locale) }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.city') }}</b>
            <div class="mg-t-11px">
              {{ newForm.currentCity }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.zipcode') }}</b>
            <div class="mg-t-11px">
              {{ newForm.currentPostcode }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Identity -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.auth_represent_identity') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-40px">
            <b>{{ $t('kyc.type_of_card') }}:</b> {{ newForm.typeOfCard === 'ID Card' ? $t('kyc.id_card') : $t('kyc.passport') }}
          </div>
          <div class="mg-t-40px is-half">
            <b>{{ newForm.typeOfCard === 'ID Card' ? $t('kyc.id_card') : $t('kyc.passport') }}</b>
            <div class="mg-t-11px">
              {{ newForm.identificationId }}
            </div>
          </div>
          <div
            v-if="newForm.laserCode"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.laser_code') }}</b>
            <div class="mg-t-11px">
              {{ newForm.laserCode }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.date_of_issue') }}</b>
            <div class="mg-t-11px">
              {{ newForm.issueDateOfId | formatDate }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.date_of_exp') }}</b>
            <div class="mg-t-11px">
              {{ displayDateOfExpire | formatDate }}
            </div>
          </div>
          <div class="mg-t-80px">
            <b>{{ $t('kyc.photo_id_card') }}</b>
            <div class="mw-600">
              <img
                :src="newForm.identificationPicture"
                class="mg-t-21px"
              >
            </div>
          </div>
          <div class="mg-t-40px">
            <b>{{ $t('kyc.photo_selfie') }}</b>
            <div class="mw-600">
              <img
                :src="newForm.selfIdentificationPicture"
                class="mg-t-21px"
              >
            </div>
          </div>
          <div
            v-if="newForm.orgCompanyCert"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.comp_cert_doc') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.orgCompanyCert.name }}
            </div>
          </div>
          <div
            v-if="newForm.orgShareholders"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.list_shareholders_doc') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.orgShareholders.name }}
            </div>
          </div>
          <div
            v-if="newForm.orgSealCert"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.seal_cert_doc') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.orgSealCert.name }}
            </div>
          </div>
          <div
            v-if="newForm.orgHouseRegisAuthDirector"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.copy_house_registration') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.orgHouseRegisAuthDirector.name }}
            </div>
          </div>
          <div
            v-if="newForm.orgPowerAttorney"
            class="mg-t-31px"
          >
            <b>{{ $t('kyc.power_attorney') }}</b>
            <div class="mg-t-11px doc-file-name">
              {{ newForm.orgPowerAttorney.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SendInfoSuccess -Modal -->
    <SendInfoSuccess :is-send-info-success="isSendInfoSuccess"/>
  </div>
</template>

<script>
import SendInfoSuccess from '../SendInfoSuccess'

export default {
  name: 'Summary',
  components: {
    SendInfoSuccess
  },
  props: {
    form: {
      type: Object,
      default: () => {}
    },
    currentStep: {
      type: Number,
      default: 0
    },
    provincesInThailand: {
      type: Array,
      default: () => []
    },
    occupationList: {
      type: Array,
      default: () => []
    },
    businessList: {
      type: Array,
      default: () => []
    },
    averageIncomeList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      newForm: {}
    }
  },
  computed: {
    isSendInfoSuccess () {
      return this.currentStep === 5
    },
    displayDateOfExpire () {
      if (this.newForm.isLifetime) {
        return this.newForm.isLifetime
      }
      return this.newForm.expireDateOfId
    }
  },
  created () {
    this.newForm = Object.assign({}, this.form)
  }
}
</script>

<style lang="scss" scoped>
* {
  font-size: 15px;
  color: #000000;
}
.tt {
  font-family: Roboto;
  font-size: 21px;
  font-weight: bold;
}
.mg-t-11px {
  margin-top: 11px;
}
.mg-t-21px {
  margin-top: 21px;
}
.mg-t-31px {
  margin-top: 31px;
}
.mg-t-40px {
  margin-top: 40px;
}
.mg-t-80px {
  margin-top: 80px;
}
.pd-l-12pct {
  padding-left: 12%;
}
.mw-600 {
  max-width: 600px;
}
.is-half {
  display: inline-block;
  width: calc((100% - 12%) / 2);
}
.divider {
  width: 100%;
  height: 1px;
  background-color: #d2d2d2;
  margin: 40px 0;
}
.doc-file-name {
  word-wrap: break-word;
  white-space: normal;
}
/* Mobile */
@media only screen and (max-width: 768px) {
  .pd-l-12pct {
    padding-left: 4%;
  }
  .is-half {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/KYC/components/Organization/Summary.vue